import { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Divider, Grid, IconButton, MenuItem, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import CurrencyInput from '../../../components/shared/CurrencyInput';
import SectionHeader from '../../../components/shared/SectionHeader';
import HistoryIcon from '@mui/icons-material/History';
import loanPricingApi from '../../../services/loanPricingApi';
import useLockDeskStatus from '../../../hooks/useLockDeskStatus';
import LockDeskClosedAlert from '../../../components/shared/LockDeskClosedAlert';
import { useToast } from '../../../components/shared/Toast';
import { getAxiosErrorMessage } from '../../../utils/getAxiosErrorMessage';

const POST_LOCK_ACTIONS = {
  LOCK_EXTENSION: {
    key: 'lockExtension',
    label: 'Lock Extension',
    icon: 'ti-clock-plus',
    description: 'Extend the current rate lock period. Typically incurs a cost based on extension duration.',
    color: 'primary',
  },
  REPRICE: {
    key: 'reprice',
    label: 'Reprice',
    icon: 'ti-refresh',
    description: 'Request new pricing at current market rates while keeping the same loan program. The existing lock is canceled and a new lock is issued.',
    color: 'blue',
  },
  RELOCK: {
    key: 'relock',
    label: 'Relock',
    icon: 'ti-lock',
    description: 'Relock the loan after the original lock has expired. Relock pricing is typically based on the worse of the original rate or current market rate.',
    color: 'cyan',
  },
  PROGRAM_CHANGE: {
    key: 'programChange',
    label: 'Program Change',
    icon: 'ti-switch-horizontal',
    description: 'Change the loan program (e.g., from 30-Year Fixed to 15-Year Fixed, or Conventional to FHA). Requires repricing under the new program.',
    color: 'purple',
  },
  CANCEL_LOCK: {
    key: 'cancelLock',
    label: 'Cancel Lock',
    icon: 'ti-lock-off',
    description: 'Cancel the current rate lock entirely. The loan returns to an unlocked state and will need to be re-priced and re-locked at current market rates.',
    color: 'red',
  },
};

const EXTENSION_DURATIONS = [
  { value: '3', label: '3 Days' },
  { value: '5', label: '5 Days' },
  { value: '7', label: '7 Days' },
  { value: '10', label: '10 Days' },
  { value: '15', label: '15 Days' },
  { value: '30', label: '30 Days' },
];

const CANCEL_REASONS = [
  { value: 'borrower_request', label: 'Borrower Requested Cancellation' },
  { value: 'better_rate', label: 'Better Rate Available (Reprice Instead)' },
  { value: 'program_change', label: 'Changing Loan Program' },
  { value: 'loan_falling_out', label: 'Loan Falling Out / Will Not Close' },
  { value: 'property_issue', label: 'Property Issue (Appraisal, Title, Inspection)' },
  { value: 'borrower_qualification', label: 'Borrower No Longer Qualifies' },
  { value: 'other', label: 'Other (explain in notes)' },
];

const PostLockActionsTab = ({ loanDetail }) => {
  const [selectedAction, setSelectedAction] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitHistory, setSubmitHistory] = useState([]);
  const [submitPostLockRequest] = loanPricingApi.endpoints.submitPostLockRequest.useMutation();
  const { isLockDeskEnabled, lockDeskStatus } = useLockDeskStatus();
  const { showToast, ToastContainer } = useToast();

  const loanData = loanDetail?.loan || {};
  const borrower = loanDetail?.borrowers?.[0] || {};
  const loanNumber = loanData.loanNumber || '';
  const currentRate = loanData.noteRate;
  const lockDate = loanData.lockConfirmedDate;
  const lockExpires = loanData.lockExpirationDate;
  const programName = loanData.programName;
  const mortgageType = loanData.mortgageType;

  const resetForm = () => {
    setFormData({});
    setSubmitted(false);
  };

  const handleSelectAction = (action) => {
    setSelectedAction(action);
    resetForm();
  };

  const handleBack = () => {
    setSelectedAction(null);
    resetForm();
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const normalizeFormData = (input) => {
    const toDisplayString = (value) => {
      if (value == null || value === '') return '—';
      if (Array.isArray(value)) return value.map(toDisplayString).join(', ');
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    };

    return Object.entries(input || {}).reduce((acc, [key, value]) => {
      acc[key] = toDisplayString(value);
      return acc;
    }, {});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLockDeskEnabled) {
      showToast('The Lock Desk is currently closed — requests cannot be submitted right now.', 'warning');
      return;
    }
    setSubmitting(true);

    try {
      const loanId = loanDetail?.loan?.loanId;
      if (!loanId) {
        showToast('Missing loan ID. Please refresh and try again.', 'danger');
        setSubmitting(false);
        return;
      }

      await submitPostLockRequest({
        loanId,
        body: {
          actionKey: selectedAction.key,
          actionLabel: selectedAction.label,
          formData: normalizeFormData(formData),
        },
      }).unwrap();

      const historyEntry = {
        id: Date.now(),
        actionType: selectedAction.label,
        loanNumber: loanNumber,
        borrowerName: `${borrower.firstName || ''} ${borrower.lastName || ''}`.trim(),
        submittedAt: new Date().toISOString(),
        formData: { ...formData },
        status: 'Pending',
      };
      setSubmitHistory(prev => [historyEntry, ...prev]);
      setSubmitting(false);
      setSubmitted(true);
      showToast(`${selectedAction.label} request submitted.`, 'success');
    } catch (err) {
      setSubmitting(false);
      showToast(getAxiosErrorMessage(err, 'Failed to submit post-lock request.'), 'danger');
    }
  };

  // --- Form renderers per action type ---

  const renderLockExtensionForm = () => (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={6}>
          <TextField select label="Extension Duration" required size="small" fullWidth value={formData.extensionDays || ''} onChange={(e) => handleFieldChange('extensionDays', e.target.value)}>
            <MenuItem value="">Select duration...</MenuItem>
            {EXTENSION_DURATIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={6}>
          <TextField label="New Lock Expiration" size="small" fullWidth disabled value={formData.extensionDays && lockExpires ? new Date(new Date(lockExpires).getTime() + parseInt(formData.extensionDays) * 86400000).toLocaleDateString() : '—'} helperText="Auto-calculated based on current expiration" />
        </Grid>
      </Grid>
      <TextField label="Reason for Extension" size="small" fullWidth multiline rows={3} placeholder="e.g., Appraisal delayed, title curative issue, borrower documentation pending..." value={formData.reason || ''} onChange={(e) => handleFieldChange('reason', e.target.value)} sx={{ mb: 2 }} />
      <FormControl sx={{ mb: 2 }}>
        <FormLabel>Willing to Accept Extension Fee?</FormLabel>
        <RadioGroup row value={formData.acceptFee || ''} onChange={(e) => handleFieldChange('acceptFee', e.target.value)}>
          <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
          <FormControlLabel value="no" control={<Radio size="small" />} label="No — Request Waiver" />
        </RadioGroup>
      </FormControl>
    </>
  );

  const renderRepriceForm = () => (
    <>
      <Alert severity="info" sx={{ mb: 2 }}><strong>Reprice</strong> cancels the current lock and issues a new lock at today's market rates. The new rate may be higher or lower than the current locked rate.</Alert>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={4}><TextField label="Current Locked Rate" size="small" fullWidth value={`${currentRate}%`} disabled /></Grid>
        <Grid size={4}><TextField label="Desired New Rate (if known)" size="small" fullWidth type="number" slotProps={{ htmlInput: { step: 0.001 } }} placeholder="e.g., 6.500" value={formData.desiredRate || ''} onChange={(e) => handleFieldChange('desiredRate', e.target.value)} helperText="Leave blank for best available" /></Grid>
        <Grid size={4}>
          <TextField select label="New Lock Period (days)" required size="small" fullWidth value={formData.newLockPeriod || ''} onChange={(e) => handleFieldChange('newLockPeriod', e.target.value)}>
            <MenuItem value="">Select...</MenuItem>
            {[15, 30, 45, 60].map(d => <MenuItem key={d} value={String(d)}>{d} Days</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={6}>
          <TextField select label="Points Preference" size="small" fullWidth value={formData.pointsPreference || ''} onChange={(e) => handleFieldChange('pointsPreference', e.target.value)}>
            <MenuItem value="">No preference</MenuItem>
            <MenuItem value="zero">Zero Points (Near Par)</MenuItem>
            <MenuItem value="buydown">Buy Down Rate (Pay Points)</MenuItem>
            <MenuItem value="lender_credit">Lender Credit (Higher Rate)</MenuItem>
          </TextField>
        </Grid>
        <Grid size={6}><TextField label="Target Closing Date" size="small" fullWidth type="date" slotProps={{ inputLabel: { shrink: true } }} value={formData.targetClosingDate || ''} onChange={(e) => handleFieldChange('targetClosingDate', e.target.value)} /></Grid>
      </Grid>
      <TextField label="Reason for Reprice" size="small" fullWidth multiline rows={3} placeholder="e.g., Market rates have improved significantly, borrower requesting better terms..." value={formData.reason || ''} onChange={(e) => handleFieldChange('reason', e.target.value)} sx={{ mb: 2 }} />
    </>
  );

  const renderRelockForm = () => (
    <>
      <Alert severity="warning" sx={{ mb: 2 }}><strong>Relock</strong> is used when the original lock has expired. Relock pricing is typically the <strong>worse of the original locked rate or current market rate</strong>, plus any applicable relock fees.</Alert>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={4}><TextField label="Original Locked Rate" size="small" fullWidth value={`${currentRate}%`} disabled /></Grid>
        <Grid size={4}><TextField label="Original Lock Expiration" size="small" fullWidth value={lockExpires ? new Date(lockExpires).toLocaleDateString() : '—'} disabled /></Grid>
        <Grid size={4}>
          <TextField select label="New Lock Period (days)" required size="small" fullWidth value={formData.newLockPeriod || ''} onChange={(e) => handleFieldChange('newLockPeriod', e.target.value)}>
            <MenuItem value="">Select...</MenuItem>
            {[15, 30, 45, 60].map(d => <MenuItem key={d} value={String(d)}>{d} Days</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={6}>
          <TextField select label="Reason Lock Expired" required size="small" fullWidth value={formData.expirationReason || ''} onChange={(e) => handleFieldChange('expirationReason', e.target.value)}>
            <MenuItem value="">Select reason...</MenuItem>
            <MenuItem value="appraisal_delay">Appraisal Delay</MenuItem>
            <MenuItem value="title_issue">Title Curative Issue</MenuItem>
            <MenuItem value="borrower_docs">Borrower Documentation Delay</MenuItem>
            <MenuItem value="underwriting_conditions">Outstanding Underwriting Conditions</MenuItem>
            <MenuItem value="closing_delay">Closing Delay (Escrow/Attorney)</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        </Grid>
        <Grid size={6}><TextField label="Expected Closing Date" required size="small" fullWidth type="date" slotProps={{ inputLabel: { shrink: true } }} value={formData.expectedClosingDate || ''} onChange={(e) => handleFieldChange('expectedClosingDate', e.target.value)} /></Grid>
      </Grid>
      <TextField label="Additional Notes" size="small" fullWidth multiline rows={3} placeholder="Any additional context for the lock desk..." value={formData.reason || ''} onChange={(e) => handleFieldChange('reason', e.target.value)} sx={{ mb: 2 }} />
    </>
  );

  const renderProgramChangeForm = () => (
    <>
      <Alert severity="info" sx={{ mb: 2 }}><strong>Program Change</strong> modifies the loan program on a locked loan. The lock desk will cancel the current lock and re-price under the new program at current market rates.</Alert>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={6}><TextField label="Current Program" size="small" fullWidth value={programName || '—'} disabled /></Grid>
        <Grid size={6}><TextField label="Current Mortgage Type" size="small" fullWidth value={mortgageType || '—'} disabled /></Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={6}>
          <TextField select label="New Mortgage Type" required size="small" fullWidth value={formData.newMortgageType || ''} onChange={(e) => handleFieldChange('newMortgageType', e.target.value)}>
            <MenuItem value="">Select...</MenuItem>
            {['Conventional', 'FHA', 'VA', 'USDA'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={6}>
          <TextField select label="New Program" required size="small" fullWidth value={formData.newProgram || ''} onChange={(e) => handleFieldChange('newProgram', e.target.value)}>
            <MenuItem value="">Select...</MenuItem>
            {['30 Year Fixed', '20 Year Fixed', '15 Year Fixed', '10 Year Fixed', '5/1 ARM', '7/1 ARM', '10/1 ARM'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={6}><CurrencyInput label="New Loan Amount (if changed)" size="small" fullWidth value={formData.newLoanAmount || ''} onChange={(v) => handleFieldChange('newLoanAmount', v)} placeholder={loanData.loanAmount?.toLocaleString()} helperText="Leave blank if unchanged" /></Grid>
        <Grid size={6}>
          <TextField select label="New Lock Period (days)" required size="small" fullWidth value={formData.newLockPeriod || ''} onChange={(e) => handleFieldChange('newLockPeriod', e.target.value)}>
            <MenuItem value="">Select...</MenuItem>
            {[15, 30, 45, 60].map(d => <MenuItem key={d} value={String(d)}>{d} Days</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>
      <TextField label="Reason for Program Change" required size="small" fullWidth multiline rows={3} placeholder="e.g., Borrower no longer qualifies under current program, better terms available under new program..." value={formData.reason || ''} onChange={(e) => handleFieldChange('reason', e.target.value)} sx={{ mb: 2 }} />
    </>
  );

  const renderCancelLockForm = () => (
    <>
      <Alert severity="error" sx={{ mb: 2 }}><strong>Lock Cancellation</strong> is permanent. The current rate and terms will be forfeited. If the loan proceeds, it will need to be re-priced and re-locked at current market rates, which may be higher than the original locked rate.</Alert>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={4}><TextField label="Current Locked Rate" size="small" fullWidth value={currentRate ? `${currentRate}%` : '—'} disabled /></Grid>
        <Grid size={4}><TextField label="Program" size="small" fullWidth value={programName || '—'} disabled /></Grid>
        <Grid size={4}><TextField label="Lock Expires" size="small" fullWidth value={lockExpires ? new Date(lockExpires).toLocaleDateString() : '—'} disabled /></Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={6}>
          <TextField select label="Reason for Cancellation" required size="small" fullWidth value={formData.cancelReason || ''} onChange={(e) => handleFieldChange('cancelReason', e.target.value)}>
            <MenuItem value="">Select reason...</MenuItem>
            {CANCEL_REASONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={6}>
          <FormControl required>
            <FormLabel>Has the borrower been informed?</FormLabel>
            <RadioGroup row value={formData.borrowerInformed || ''} onChange={(e) => handleFieldChange('borrowerInformed', e.target.value)}>
              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value="no" control={<Radio size="small" />} label="Not Yet" />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={6}>
          <FormControl required>
            <FormLabel>Do you intend to relock this loan?</FormLabel>
            <RadioGroup row value={formData.intendRelock || ''} onChange={(e) => handleFieldChange('intendRelock', e.target.value)}>
              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes, will relock" />
              <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
              <FormControlLabel value="unsure" control={<Radio size="small" />} label="Unsure" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid size={6}><TextField label="Expected Relock / Closing Timeline" size="small" fullWidth type="date" slotProps={{ inputLabel: { shrink: true } }} value={formData.expectedTimeline || ''} onChange={(e) => handleFieldChange('expectedTimeline', e.target.value)} helperText="When do you expect to relock or close?" /></Grid>
      </Grid>
      <TextField label="Additional Details" required size="small" fullWidth multiline rows={3} placeholder="Provide any additional context for the lock desk — e.g., circumstances leading to cancellation, any outstanding conditions, borrower situation..." value={formData.reason || ''} onChange={(e) => handleFieldChange('reason', e.target.value)} sx={{ mb: 2 }} />
    </>
  );

  const renderForm = () => {
    switch (selectedAction.key) {
      case 'lockExtension': return renderLockExtensionForm();
      case 'reprice': return renderRepriceForm();
      case 'relock': return renderRelockForm();
      case 'programChange': return renderProgramChangeForm();
      case 'cancelLock': return renderCancelLockForm();
      default: return null;
    }
  };

  // --- Main render ---

  if (!loanDetail) return null;

  return (
    <Box>
      {ToastContainer}
      {!isLockDeskEnabled && <LockDeskClosedAlert lockDeskStatus={lockDeskStatus} />}
      {/* Lock Confirmation */}
      <Card variant="outlined" sx={{ mb: 3, borderTop: 3, borderTopColor: 'success.main' }}>
        <CardHeader
          avatar={<LockIcon color="success" />}
          title={<SectionHeader title="Lock Confirmation" icon={<LockIcon fontSize="small" />} sx={{ mb: 0 }} />}
          action={<Typography variant="caption" color="text.secondary">{loanNumber} — {borrower.firstName} {borrower.lastName}</Typography>}
        />
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ textAlign: 'center', pr: 3 }}>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase">Locked Rate</Typography>
              <Typography variant="h4" color="primary.main">{currentRate ? `${currentRate}%` : '—'}</Typography>
              {loanData.rateLockPeriod && <Typography variant="caption" color="text.secondary">{loanData.rateLockPeriod}-day lock</Typography>}
            </Box>
            <Divider orientation="vertical" flexItem />
            <Grid container spacing={2} sx={{ flex: 1 }}>
              <Grid size={3}><Typography variant="caption" color="text.secondary" textTransform="uppercase">Program</Typography><Typography variant="body2" fontWeight={700}>{programName || '—'}</Typography></Grid>
              <Grid size={3}><Typography variant="caption" color="text.secondary" textTransform="uppercase">Lock Date</Typography><Typography variant="body2" fontWeight={700}>{lockDate ? new Date(lockDate).toLocaleDateString() : '—'}</Typography></Grid>
              <Grid size={3}><Typography variant="caption" color="text.secondary" textTransform="uppercase">Expires</Typography><Typography variant="body2" fontWeight={700}>{lockExpires ? new Date(lockExpires).toLocaleDateString() : '—'}</Typography></Grid>
              <Grid size={3}>
                <Typography variant="caption" color="text.secondary" textTransform="uppercase">Lock Status</Typography>
                <Box>{(() => {
                  if (!lockExpires) return '—';
                  const exp = new Date(lockExpires);
                  const now = new Date();
                  const daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
                  if (daysLeft < 0) return <Chip label="Expired" size="small" color="error" variant="outlined" />;
                  if (daysLeft <= 7) return <Chip label={`${daysLeft} days left`} size="small" color="warning" variant="outlined" />;
                  return <Chip label={`${daysLeft} days left`} size="small" color="success" variant="outlined" />;
                })()}</Box>
              </Grid>
            </Grid>
          </Stack>

          <Divider sx={{ my: 1 }} />

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={6}>
              <Typography variant="overline" color="text.secondary">Pricing</Typography>
              <Table size="small" sx={{ '& td': { border: 0, py: 0.3, px: 0 } }}>
                <TableBody>
                  {loanData.basePrice != null && (
                    <TableRow><TableCell><Typography variant="body2" color="text.secondary">Base Price</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{Number(loanData.basePrice).toFixed(3)}</Typography></TableCell></TableRow>
                  )}
                  {loanData.loanOfficerPrice != null && (
                    <TableRow><TableCell><Typography variant="body2" color="text.secondary">Adjusted Price (Net)</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{Number(loanData.loanOfficerPrice).toFixed(3)}</Typography></TableCell></TableRow>
                  )}
                  {loanData.basePrice != null && loanData.loanOfficerPrice != null && (
                    <TableRow><TableCell><Typography variant="body2" color="text.secondary">Price Adjustments</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700} color={(loanData.loanOfficerPrice - loanData.basePrice) < 0 ? 'error.main' : 'success.main'}>{(loanData.loanOfficerPrice - loanData.basePrice) >= 0 ? '+' : ''}{(loanData.loanOfficerPrice - loanData.basePrice).toFixed(3)}</Typography></TableCell></TableRow>
                  )}
                  {loanData.basePrice != null && loanData.loanAmount > 0 && (() => {
                    const netPrice = loanData.loanOfficerPrice ?? loanData.basePrice;
                    const creditOrCost = ((netPrice - 100) / 100) * loanData.loanAmount;
                    return (
                      <TableRow sx={{ borderTop: 1, borderColor: 'divider' }}><TableCell><Typography variant="body2" color="text.secondary">{creditOrCost >= 0 ? 'Lender Credit' : 'Discount Points'}</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700} color={creditOrCost >= 0 ? 'success.main' : 'error.main'}>{creditOrCost >= 0 ? '+' : ''}${Math.abs(creditOrCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography></TableCell></TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </Grid>
            <Grid size={6}>
              <Typography variant="overline" color="text.secondary">Compensation</Typography>
              {(() => {
                const paidBy = loanData.compensationPaidByType;
                const isLpc = paidBy?.toLowerCase() === 'lender';
                const isBpc = paidBy?.toLowerCase() === 'borrower';
                const compPercent = isLpc ? loanData.lenderPaidCompensationPercent : isBpc ? loanData.borrowerPaidCompensationPercent : null;
                const compAmount = isLpc ? loanData.lenderPaidCompensationAmount : isBpc ? loanData.borrowerPaidCompensationAmount : null;
                const hasComp = compPercent != null || compAmount != null || (loanData.brokerCompensation != null && loanData.brokerCompensation > 0);
                if (!hasComp) return <Typography variant="body2" color="text.secondary" fontStyle="italic">Compensation not yet calculated on this loan.</Typography>;
                return (
                  <Table size="small" sx={{ '& td': { border: 0, py: 0.3, px: 0 } }}>
                    <TableBody>
                      {paidBy && <TableRow><TableCell><Typography variant="body2" color="text.secondary">Paid By</Typography></TableCell><TableCell align="right"><Chip label={isLpc ? 'Lender (LPC)' : isBpc ? 'Borrower (BPC)' : paidBy} size="small" color={isLpc ? 'info' : 'secondary'} variant="outlined" /></TableCell></TableRow>}
                      {compPercent != null && <TableRow><TableCell><Typography variant="body2" color="text.secondary">Comp %</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{Number(compPercent).toFixed(3)}%</Typography></TableCell></TableRow>}
                      {compAmount != null && compAmount > 0 && <TableRow><TableCell><Typography variant="body2" color="text.secondary">Comp Amount</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700} color="success.main">${Number(compAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography></TableCell></TableRow>}
                      {!compAmount && loanData.brokerCompensation != null && loanData.brokerCompensation > 0 && <TableRow><TableCell><Typography variant="body2" color="text.secondary">Expected Comp</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700} color="success.main">${Number(loanData.brokerCompensation).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography></TableCell></TableRow>}
                    </TableBody>
                  </Table>
                );
              })()}
            </Grid>
          </Grid>

          {loanData.quotedAtDateTime && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 1 }}>Priced {new Date(loanData.quotedAtDateTime).toLocaleString()}</Typography>
          )}
        </CardContent>
      </Card>

      {/* Action selection or form */}
      {!selectedAction ? (
        <>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Post-Lock Actions</Typography>
          <Grid container spacing={2}>
            {Object.values(POST_LOCK_ACTIONS).map(action => (
              <Grid key={action.key} size={{ xs: 12, md: 4 }}>
                <Card variant="outlined" sx={{ cursor: 'pointer', '&:hover': { boxShadow: 2 }, transition: 'box-shadow 0.2s' }} onClick={() => handleSelectAction(action)}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${action.color}.50` }}>
                        <LockIcon sx={{ fontSize: 18, color: `${action.color}.main` }} />
                      </Box>
                      <Typography variant="subtitle2">{action.label}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>{action.description}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {submitHistory.length > 0 && (
            <Card variant="outlined" sx={{ mt: 3 }}>
              <CardHeader title={<SectionHeader title="Request History" icon={<HistoryIcon fontSize="small" />} sx={{ mb: 0 }} />} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submitHistory.map(entry => (
                    <TableRow key={entry.id}>
                      <TableCell><Typography variant="body2" fontWeight={700}>{entry.actionType}</Typography></TableCell>
                      <TableCell>{new Date(entry.submittedAt).toLocaleString()}</TableCell>
                      <TableCell><Chip label={entry.status} size="small" color="warning" variant="outlined" /></TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>{entry.formData.reason || entry.formData.exceptionReason || '—'}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      ) : submitted ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>Request Submitted</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your <strong>{selectedAction.label}</strong> request has been emailed to the lock desk for <strong>{loanNumber}</strong>.<br />They will review and respond typically within 1-2 business hours.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={handleBack}>Back to Post-Lock Actions</Button>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { resetForm(); }}>Submit Another {selectedAction.label}</Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Card variant="outlined">
          <CardHeader
            avatar={<IconButton size="small" onClick={handleBack}><ArrowBackIcon /></IconButton>}
            title={<SectionHeader title={selectedAction.label} sx={{ mb: 0 }} />}
            subheader={selectedAction.description}
            subheaderTypographyProps={{ variant: 'caption' }}
          />
          <CardContent>
            <form onSubmit={handleSubmit}>
              {renderForm()}

              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={6}>
                  <TextField select label="Urgency" size="small" fullWidth value={formData.urgency || 'normal'} onChange={(e) => handleFieldChange('urgency', e.target.value)}>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="urgent">Urgent — Closing within 5 days</MenuItem>
                    <MenuItem value="critical">Critical — Closing within 48 hours</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={6}>
                  <TextField select label="Preferred Contact Method for Response" size="small" fullWidth value={formData.contactMethod || 'email'} onChange={(e) => handleFieldChange('contactMethod', e.target.value)}>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="phone">Phone Call</MenuItem>
                    <MenuItem value="both">Both Email & Phone</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="text" onClick={handleBack}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={submitting || !isLockDeskEnabled} startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />} title={!isLockDeskEnabled ? 'The Lock Desk is currently closed' : ''}>
                  {!isLockDeskEnabled ? 'Lock Desk Closed' : submitting ? 'Submitting...' : 'Submit to Lock Desk'}
                </Button>
              </Stack>
            </form>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PostLockActionsTab;
